import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { screenOf, startUsageTracking, stopUsageTracking, tabOf, takeNav, track } from "../lib/usageTracker";

/**
 * Mounts the usage tracker (#1110, D218) and reports each screen change.
 * Renders nothing. Mounted once beside `ScrollToTop` so it sees every route.
 *
 * Tracking starts only for a signed-in player or coach; the tracker then asks
 * the server whether this account is counted and stays silent if not. Signed
 * out (or an admin) stops it and clears anything waiting.
 *
 * A screen view is sent on each pathname or allowed-tab change, with how the
 * person got there: the source a control marked just before navigating, else
 * `back` for a browser Back after the first view, else `deep_link` for the
 * first view after load, else `in_page`. Only the route template and an
 * allowed tab word leave this component, never the path or query string.
 */
const COUNTED_ROLES = ["player", "coach"];

export function UsageTracker() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const role = user && typeof user === "object" ? user.role : null;
  const tracking = COUNTED_ROLES.includes(role);
  const sentFirst = useRef(false);

  // Declared before the screen effect so the tracker is listening first.
  useEffect(() => {
    if (!tracking) return undefined;
    startUsageTracking();
    return () => stopUsageTracking();
    // `role` too: a hat switch asks the server again, because an account that
    // holds the admin role is not counted in any hat.
  }, [tracking, role]);

  const screen = screenOf(pathname);
  const tab = screen ? tabOf(screen, search) : null;

  useEffect(() => {
    if (!tracking || !screen) return;
    const marked = takeNav();
    let via = marked;
    if (!via) {
      if (!sentFirst.current) via = "deep_link";
      else via = navType === "POP" ? "back" : "in_page";
    }
    sentFirst.current = true;
    track("screen_view", { screen, tab, via });
    // `navType` is read, not a trigger: only a pathname or tab change reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking, pathname, tab]);

  return null;
}

export default UsageTracker;
